import { useState, useEffect } from 'react';
import {
	Popover,
	Comment,
	Avatar,
	Form,
	Input,
	Button,
	Tooltip,
	Popconfirm,
} from 'antd';
import { LuBadgeAlert } from 'react-icons/lu';
import { EyeInvisibleOutlined } from '@ant-design/icons';
import { USER_ROLES } from 'src/_shared/constants/userRolesEnum';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { motion } from 'motion/react';
import recipientIcon from './../.../../../../assets/svg/recipient.svg';
import clapIcon from './../.../../../../assets/svg/clap.svg';
import clapBlue from './../../../assets/svg/clap_blue.svg';
import { CoreBadge } from '../../../RnRCommonComponent';
import { LuCornerDownRight } from 'react-icons/lu';
import uuid from 'uuid/v4';
import {
	addCommas,
	getRecognizerName,
	isAutomatedRecognizer,
} from '../../../rewardsAndRecognitionHelper/utils.js';
import {
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
} from 'src/RnRCommonComponent/CoreBadge/rewardsConstants';

const { TextArea } = Input;

dayjs.extend(relativeTime);

const getInitials = (user) => {
	const firstNameInitial = user?.firstName
		? user.firstName.charAt(0).toUpperCase()
		: '';
	const lastNameInitial = user?.lastName
		? user.lastName.charAt(0).toUpperCase()
		: '';
	return `${firstNameInitial}${lastNameInitial}`;
};

const Editor = ({ onChange, onSubmit, value, onCancel }) => (
	<>
		<Form.Item style={{ marginBottom: '12px' }}>
			<TextArea
				rows={3}
				onChange={onChange}
				value={value}
				placeholder="Write a reply..."
			/>
		</Form.Item>
		<Form.Item style={{ marginBottom: '0' }}>
			<div style={{ display: 'flex', gap: '8px' }}>
				<Button
					htmlType="submit"
					onClick={onSubmit}
					type="primary"
					size="small"
				>
					Reply
				</Button>
				<Button onClick={onCancel} size="small">
					Cancel
				</Button>
			</div>
		</Form.Item>
	</>
);

const getMilestoneAvatarColor = (milestoneYears, milestoneColorMap) => {
	if (!milestoneColorMap || milestoneColorMap.length === 0) return null;

	if (milestoneYears == null) {
		const defaultEntry = milestoneColorMap.find((entry) => entry.years === 0);
		return defaultEntry?.color || null;
	}

	const sorted = [...milestoneColorMap]
		.filter((entry) => entry.years <= milestoneYears)
		.sort((a, b) => b.years - a.years);

	return sorted[0]?.color || null;
};

const FeedList = (props) => {
	const {
		feedListData = [],
		handleClapCount,
		userAvatarSrc,
		handleReplySubmit: submitReply,
		handleSetToPrivate,
		currentUser,
		handleSelectTags,
		availableTags = [],
		isFeedLoading = false,
		isLoadingMore = false,
	} = props;

	const [replyStates, setReplyStates] = useState({});
	const [indexOffset, setIndexOffset] = useState(0);
	const disableReplies =
		currentUser?.company?.recognitionSettings?.disableReplies;
	const milestoneColorMap =
		currentUser?.company?.recognitionSettings?.milestoneColorMap;

	const isAdmin =
		currentUser?.role === USER_ROLES.ADMIN ||
		currentUser?.role === USER_ROLES.SUPPORT_ADMIN ||
		currentUser?.role === USER_ROLES.SUPER_ADMIN;

	useEffect(() => {
		if (isLoadingMore) {
			// Offset used to reset cascading entrance animation delay for paginated data
			setIndexOffset(feedListData.length);
		}
	}, [isLoadingMore]);

	const toggleReplyEditor = (itemId) => {
		setReplyStates((prev) => ({
			...prev,
			[itemId]: {
				...prev[itemId],
				showEditor: !prev[itemId]?.showEditor,
				value: prev[itemId]?.value || '',
			},
		}));
	};

	const handleReplyChange = (itemId, e) => {
		setReplyStates((prev) => ({
			...prev,
			[itemId]: {
				...prev[itemId],
				value: e.target.value,
			},
		}));
	};

	const handleReplySubmit = async (itemId) => {
		const replyText = replyStates[itemId]?.value?.trim();
		if (!replyText) return;

		// Clear editor immediately for snappy optimistic UX
		setReplyStates((prev) => ({
			...prev,
			[itemId]: { showEditor: false, value: '' },
		}));

		try {
			const success = await submitReply(itemId, replyText);
			if (!success) {
				setReplyStates((prev) => ({
					...prev,
					[itemId]: { showEditor: true, value: replyText },
				}));
			}
		} catch (error) {
			console.error('Error submitting reply:', error);
			setReplyStates((prev) => ({
				...prev,
				[itemId]: { showEditor: true, value: replyText },
			}));
		}
	};

	const cancelReply = (itemId) => {
		setReplyStates((prev) => ({
			...prev,
			[itemId]: {
				showEditor: false,
				value: '',
			},
		}));
	};

	const formatRecipients = (recipients) => {
		if (!recipients || recipients.length === 0) return '';

		if (recipients.length === 1) {
			return `${recipients[0].firstName} ${recipients[0].lastName}`;
		}

		if (recipients.length <= 3) {
			return recipients
				.map((recipient) => `${recipient.firstName} ${recipient.lastName}`)
				.join(', ');
		}

		const firstThree = recipients
			.slice(0, 3)
			.map((recipient) => `${recipient.firstName} ${recipient.lastName}`)
			.join(', ');
		return `${firstThree} and ${recipients.length - 3} others`;
	};

	const recipientPopoverContent = (recipients) => {
		if (!recipients || recipients.length <= 3) return null;

		return (
			<div
				style={{
					maxHeight: '300px',
					overflowY: 'scroll',
					textOverflow: 'ellipsis',
					width: '130px',
				}}
			>
				{recipients?.map((recipient) => (
					<div className="feed-author" key={recipient.id}>
						<div
							className="feed-img"
							style={{ display: 'inline-block', margin: '0 4px 4px 0' }}
						>
							<img src={recipientIcon} alt="img" />
						</div>
						<span>
							{recipient.firstName} {recipient.lastName}
						</span>
					</div>
				))}
			</div>
		);
	};

	const reactionDetails = (reactionNames, reactionCount, hasClapped) => {
		if (reactionCount === 0 && !hasClapped) return null;
		const popoverContent = () => {
			return parseInt(reactionCount) ? (
				<div
					style={{
						maxHeight: '300px',
						overflowY: 'scroll',
						textOverflow: 'ellipsis',
						width: '130px',
					}}
				>
					{reactionNames?.map((name) => (
						<div className="feed-author">
							<div
								className="feed-img"
								style={{ display: 'inline-block', margin: '0 4px 4px 0' }}
							>
								<img src={recipientIcon} alt="img" />
							</div>
							<span key={uuid()}>{name}</span>
						</div>
					))}
				</div>
			) : null;
		};

		return (
			<div style={{ fontSize: '12px', marginRight: '6px' }}>
				<Popover
					overlayInnerStyle={{ width: '100%' }}
					trigger="hover"
					content={reactionCount >= 2 ? popoverContent : null}
					getPopupContainer={(trigger) => trigger.parentNode}
				>
					<span style={{ cursor: 'default' }}>
						{(hasClapped ? 'You' : reactionNames[0]) +
							(reactionCount > 2 ? ` and ${reactionCount} others` : '') +
							(reactionCount === 2 ? ` and 1 other` : '') +
							(hasClapped ? ' clapped' : '')}
					</span>
				</Popover>
			</div>
		);
	};

	const renderMessageWithHashtags = (text) => {
		if (!text || !availableTags || availableTags.length === 0) {
			return text;
		}

		const tagPattern = availableTags
			.map((tag) => `#${tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`)
			.join('|');
		const regex = new RegExp(`(${tagPattern})`, 'g'); // # + tag + (whitespace or end of string)

		const parts = [];
		let lastIndex = 0;
		regex.lastIndex = 0;
		let match;

		while ((match = regex.exec(text)) !== null) {
			if (match.index > lastIndex) {
				parts.push(text.substring(lastIndex, match.index));
			}

			const tagWithHash = match[0];
			const tag = tagWithHash.substring(1);

			parts.push(
				<span
					key={`${match.index}-${tag}`}
					onClick={() => {
						handleSelectTags([tag]);
						// Timeout with 0ms lets smooth scroll jump to top of animation queue
						setTimeout(() => {
							try {
								const scrollContainer =
									document.querySelector('.container-fluid');
								const feedContainer = document.querySelector('.feed-container');
								if (scrollContainer && feedContainer) {
									const feedTop = feedContainer.offsetTop;
									const currentScrollPosition = scrollContainer.scrollTop;
									if (feedTop < currentScrollPosition) {
										scrollContainer.scrollTo({
											top: feedTop,
											behavior: 'smooth',
										});
									}
								}
							} catch (e) {
								console.error(e);
							}
						}, 0);
					}}
					onMouseEnter={(e) => {
						e.currentTarget.style.color = '#7dc5ffff';
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.color = '#1890ff';
					}}
					style={{
						color: '#1890ff',
						cursor: 'pointer',
						transition: 'color 0.3s ease',
					}}
				>
					{tagWithHash}
				</span>
			);

			lastIndex = match.index + match[0].length;
		}

		if (lastIndex < text.length) {
			parts.push(text.substring(lastIndex));
		}

		return parts.length > 0 ? parts : text;
	};

	return (
		<>
			{!isFeedLoading && feedListData.length === 0 && (
				<div
					style={{
						display: 'flex',
						gap: '4px',
						padding: '20px',
						color: '#595959',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<LuBadgeAlert />
					<span>No matching recognitions were found.</span>
				</div>
			)}
			{feedListData &&
				feedListData?.map((item, index) => {
					const points = item?.points ?? 0;
					const messageText = item?.content?.message || '';
					const messageImage = item?.content?.gifURL || null;

					const comments =
						typeof item?.comments === 'string'
							? JSON.parse(item?.comments)
							: [];
					let commentsList = item?.commentsList || [];

					// for optimistic updates to comments
					if (comments?.length > commentsList?.length) {
						const newComment = {
							...comments[comments.length - 1],
							user: { avatarSignedUrl: userAvatarSrc || null },
						};
						commentsList = [...commentsList, newComment];
					}

					const hasClapped =
						item?.hasClappedUsers?.some(
							(user) => user.id === currentUser?.id
						) || false;

					const addOrRemoveClap = () => handleClapCount(item.id);
					const formatedCreatedAt = dayjs(item?.createdAt).fromNow();
					const reactionNames =
						item?.hasClappedUsers
							?.filter((user) => user.id !== currentUser?.id)
							.map((user) => `${user.firstName} ${user.lastName}`) || [];
					const reactionCount = (item?.totalClaps ?? 0) - (hasClapped ? 1 : 0);
					const renderReactions = reactionDetails(
						reactionNames,
						reactionCount,
						hasClapped
					);

					const badge = item?.badge || {};
					const { label, color, gradientColor } = badge;
					const badgeTypeCode = badge?.badgeTypeId
						? REVERSE_MAPING_BADGE_TYPE[badge.badgeTypeId]
						: null;
					const iconData = badge?.iconId
						? ICON_COMPLETE_MAP.find((icon) => icon.id === badge.iconId)
						: null;
					const iconCode = iconData ? iconData.code : null;

					const mainRecipient = item?.user || item?.accountClaim;

					const recipients = (
						item?.recipients || [
							{
								user: mainRecipient,
							},
						]
					).map((recipient) => ({
						id: recipient?.user?.id || recipient?.accountClaim?.id,
						firstName:
							recipient?.user?.firstName || recipient?.accountClaim?.firstName,
						lastName:
							recipient?.user?.lastName || recipient?.accountClaim?.lastName,
					}));

					const recipientText = formatRecipients(recipients);
					const sentByName = (
						<div style={{ display: 'flex', flexDirection: 'row' }}>
							<div>
								<span>
									{getRecognizerName(
										item?.awardedBy,
										item?.awardedByUser,
										currentUser?.company?.name
									)}
								</span>
								<Popover
									trigger="hover"
									content={dayjs(item?.createdAt).format('MMMM D, YYYY h:mm A')}
									getPopupContainer={(trigger) => trigger.parentNode}
								>
									<span
										style={{
											marginLeft: '8px',
											color: '#ccc',
											fontSize: '12px',
											cursor: 'default',
										}}
									>
										{formatedCreatedAt}
									</span>
								</Popover>
								<div
									style={{
										marginLeft: '4px',
										display: 'flex',
										alignItems: 'baseline',
									}}
								>
									<LuCornerDownRight />
									{recipients.length > 3 ? (
										<Popover
											overlayInnerStyle={{ width: '100%' }}
											trigger="hover"
											content={recipientPopoverContent(recipients)}
											getPopupContainer={(trigger) => trigger.parentNode}
										>
											<span style={{ marginLeft: 4, cursor: 'default' }}>
												@{recipientText}
											</span>
										</Popover>
									) : (
										<span style={{ marginLeft: 4 }}>@{recipientText}</span>
									)}
									{points > 0 && (
										<span
											style={{
												color: '#52c41a',
												fontWeight: 'bold',
												fontSize: '12px',
												whiteSpace: 'nowrap',
												marginLeft: '6px',
											}}
										>
											+{addCommas(points)} points
										</span>
									)}
								</div>
							</div>
							<div style={{ display: 'inline-flex', marginLeft: '4px' }}>
								{badgeTypeCode && (
									<div
										style={{
											width: '36px',
											height: '36px',
											overflow: 'visible',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											flexShrink: 0,
											cursor: 'pointer',
										}}
										onMouseEnter={(e) => {
											const badgeElement =
												e.currentTarget.querySelector('.badge-transform');
											if (badgeElement) {
												badgeElement.style.transform = 'scale(0.9)';
											}
										}}
										onMouseLeave={(e) => {
											const badgeElement =
												e.currentTarget.querySelector('.badge-transform');
											if (badgeElement) {
												badgeElement.style.transform = 'scale(0.2)';
											}
										}}
									>
										<div
											className="badge-transform"
											style={{
												transform: 'scale(0.2)',
												transformOrigin: 'center',
												transition: 'transform 0.3s ease',
											}}
										>
											<CoreBadge
												useTilt={false}
												badgeType={badgeTypeCode}
												badgeIcon={iconCode}
												label={label}
												color={color}
												gradientColor={gradientColor}
												shape={badge.shape}
												badgeRotation={badge.badgeRotation}
												customImageSignedUrl={badge.customImageSignedUrl}
												labelColor={badge.labelColor}
												customIconSignedUrl={badge.customIconSignedUrl}
											/>
										</div>
									</div>
								)}
							</div>
						</div>
					);

					const content = (
						<div>
							{messageText && (
								<div
									style={{
										wordWrap: 'break-word',
										marginBottom: messageImage ? '12px' : '0',
									}}
								>
									{renderMessageWithHashtags(messageText)}
								</div>
							)}
							{messageImage && (
								<div
									style={{
										display: 'flex',
										justifyContent: 'center',
										marginTop: '8px',
									}}
								>
									<img
										src={messageImage}
										style={{ maxWidth: '100%', maxHeight: '236px' }}
										alt="Feed content"
									/>
								</div>
							)}
						</div>
					);

					const actions = [
						<div
							key="clap"
							style={{
								display: 'flex',
								alignItems: 'center',
								height: '21px',
							}}
						>
							<span
								onClick={addOrRemoveClap}
								style={{ cursor: 'pointer', marginRight: 4 }}
							>
								<img
									src={hasClapped ? clapBlue : clapIcon}
									style={{ verticalAlign: 'bottom' }}
									className="clap-icon"
									alt="clap"
								/>
							</span>
							{renderReactions || ''}
						</div>,
						!disableReplies ? (
							<span
								key="reply"
								onClick={() => toggleReplyEditor(item.id)}
								style={{ cursor: 'pointer', fontWeight: 900 }}
							>
								Reply
							</span>
						) : null,
					];

					const replies = commentsList.map((comment) => ({
						id: comment.userId + '_' + comment.createdAt,
						sentBy: {
							id: comment.userId,
							firstName: comment.firstName,
							lastName: comment.lastName,
							avatar: comment?.user?.avatarSignedUrl || null,
							milestoneYears: comment?.user?.milestoneYears ?? null,
						},
						content: {
							message: comment.message,
							gifURL: null,
						},
						createdAt: comment.createdAt,
					}));

					return (
						<motion.div
							key={item.id || index}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{
								duration: 0.5,
								delay: (index - indexOffset) * 0.06,
								ease: 'easeOut',
							}}
						>
							<div
								className="feed-card"
								style={{ marginBottom: '16px', position: 'relative' }}
							>
								{isAdmin && handleSetToPrivate && (
									<Popconfirm
										title={
											<div
												style={{
													display: 'flex',
													flexDirection: 'column',
													gap: '8px',
												}}
											>
												<div>
													Are you sure you want to mark this Recognition as
													private?
												</div>
												<div>
													This action will remove it from all feeds, for all
													users.
												</div>
											</div>
										}
										onConfirm={() => handleSetToPrivate(item.id)}
										okText="Yes"
										cancelText="No"
										placement="bottomRight"
										getPopupContainer={() =>
											document.getElementsByClassName('feed-container')?.[0]
										}
									>
										<Tooltip
											title="Remove from feed"
											placement="top"
											getPopupContainer={() =>
												document.getElementsByClassName('feed-container')?.[0]
											}
										>
											<Button
												type="text"
												icon={<EyeInvisibleOutlined />}
												size="small"
												style={{
													position: 'absolute',
													top: '8px',
													right: '8px',
													zIndex: 10,
													color: '#595959',
												}}
											/>
										</Tooltip>
									</Popconfirm>
								)}
								<Comment
									author={sentByName}
									avatar={(() => {
										if (isAutomatedRecognizer(item?.awardedBy)) {
											return <Avatar>{currentUser?.company?.name?.[0]}</Avatar>;
										}

										const avatarColor = getMilestoneAvatarColor(
											item?.awardedByUser?.milestoneYears,
											milestoneColorMap
										);

										if (item?.awardedByUser?.avatarSignedUrl) {
											return avatarColor ? (
												<div style={{ position: 'relative', display: 'inline-flex' }}>
													<Avatar src={item.awardedByUser.avatarSignedUrl} />
													<div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `3px solid ${avatarColor}`, boxShadow: `0 0 0 0.5px ${avatarColor}`, pointerEvents: 'none' }} />
												</div>
											) : (
												<Avatar src={item.awardedByUser.avatarSignedUrl} />
											);
										}

										return avatarColor ? (
											<Avatar style={{ backgroundColor: avatarColor }}>{getInitials(item?.awardedByUser)}</Avatar>
										) : (
											<Avatar>{getInitials(item?.awardedByUser)}</Avatar>
										);
									})()}
									content={content}
									actions={actions}
								>
									{!disableReplies &&
										replies.map((reply) => {
											const replyAuthor = `${reply.sentBy.firstName} ${reply.sentBy.lastName}`;
											const replyContent = reply.content.message;
											const replyDate = dayjs(reply.createdAt).fromNow();

											return (
												<Comment
													key={reply.id}
													author={replyAuthor}
													avatar={(() => {
														const replyAvatarColor = getMilestoneAvatarColor(
															reply?.sentBy?.milestoneYears,
															milestoneColorMap
														);
														if (reply?.sentBy?.avatar) {
															return replyAvatarColor ? (
																<div style={{ position: 'relative', display: 'inline-flex' }}>
																	<Avatar src={reply.sentBy.avatar} />
																	<div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `3px solid ${replyAvatarColor}`, boxShadow: `0 0 0 0.5px ${replyAvatarColor}`, pointerEvents: 'none' }} />
																</div>
															) : (
																<Avatar src={reply.sentBy.avatar} />
															);
														}
														return replyAvatarColor ? (
															<Avatar style={{ backgroundColor: replyAvatarColor }}>{getInitials(reply?.sentBy)}</Avatar>
														) : (
															<Avatar>{getInitials(reply?.sentBy)}</Avatar>
														);
													})()}
													content={
														<div style={{ wordWrap: 'break-word' }}>
															{replyContent}
														</div>
													}
													datetime={replyDate}
												/>
											);
										})}

									{replyStates[item.id]?.showEditor && (
										<Comment
											avatar={(() => {
												const editorAvatarColor = getMilestoneAvatarColor(
													currentUser?.milestoneYears,
													milestoneColorMap
												);
												if (userAvatarSrc) {
													return editorAvatarColor ? (
														<div style={{ position: 'relative', display: 'inline-flex' }}>
															<Avatar src={userAvatarSrc} />
															<div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `3px solid ${editorAvatarColor}`, boxShadow: `0 0 0 0.5px ${editorAvatarColor}`, pointerEvents: 'none' }} />
														</div>
													) : (
														<Avatar src={userAvatarSrc} />
													);
												}
												return editorAvatarColor ? (
													<Avatar style={{ backgroundColor: editorAvatarColor }}>{getInitials(currentUser)}</Avatar>
												) : (
													<Avatar>{getInitials(currentUser)}</Avatar>
												);
											})()}
											content={
												<Editor
													onChange={(e) => handleReplyChange(item.id, e)}
													onSubmit={() => handleReplySubmit(item.id)}
													value={replyStates[item.id]?.value || ''}
													onCancel={() => cancelReply(item.id)}
												/>
											}
										/>
									)}
								</Comment>
							</div>
							{index < feedListData.length - 1 && (
								<div
									className="comment-spacer"
									style={{
										height: '0px',
										borderBottom: '1px solid #e0e0e0',
										margin: '16px 0',
										width: '100%',
									}}
								/>
							)}
						</motion.div>
					);
				})}
		</>
	);
};

export default FeedList;
